import { db, type SqliteAsyncConnection } from '../../shared/localDb';
import type { PurchasesPullPayload } from './purchasesPullRemote';

function str(val: unknown, fallback = ''): string {
  return val != null ? String(val) : fallback;
}

function strOrNull(val: unknown): string | null {
  return val != null ? String(val) : null;
}

function num(val: unknown, fallback = 0): number {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

export async function upsertPulledPurchaseOrderLocal(
  raw: Record<string, unknown>,
  conn?: SqliteAsyncConnection,
): Promise<void> {
  const exec = conn ?? db;
  await exec.runAsync(
    `INSERT OR REPLACE INTO purchase_orders (
      id, organization_id, branch_id, supplier_id, supplier_name, status, origin, order_number, total, notes,
      expected_delivery, supplier_invoice_ref, created_at, created_by,
      updated_at, updated_by, received_at, received_by,
      voided_at, voided_by, void_reason, closed_at, closed_by, close_reason, synced
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`,
    str(raw.id),
    strOrNull(raw.organization_id),
    str(raw.branch_id),
    strOrNull(raw.supplier_id),
    strOrNull(raw.supplier_name),
    str(raw.status, 'draft'),
    str(raw.origin, 'order'),
    strOrNull(raw.order_number),
    num(raw.total),
    strOrNull(raw.notes),
    strOrNull(raw.expected_delivery),
    strOrNull(raw.supplier_invoice_ref),
    str(raw.created_at),
    strOrNull(raw.created_by),
    str(raw.updated_at),
    strOrNull(raw.updated_by),
    strOrNull(raw.received_at),
    strOrNull(raw.received_by),
    strOrNull(raw.voided_at),
    strOrNull(raw.voided_by),
    strOrNull(raw.void_reason),
    strOrNull(raw.closed_at),
    strOrNull(raw.closed_by),
    strOrNull(raw.close_reason),
  );
}

export async function upsertPulledPurchaseOrderItemLocal(
  raw: Record<string, unknown>,
  conn?: SqliteAsyncConnection,
): Promise<void> {
  const exec = conn ?? db;
  await exec.runAsync(
    `INSERT OR REPLACE INTO purchase_order_items (
      id, purchase_order_id, product_id, product_name, product_sku,
      quantity, unit_cost, unit_of_measure, received_quantity, target_branch_id, variant_id, synced
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`,
    str(raw.id),
    str(raw.purchase_order_id),
    strOrNull(raw.product_id),
    strOrNull(raw.product_name),
    strOrNull(raw.product_sku),
    num(raw.quantity),
    num(raw.unit_cost),
    strOrNull(raw.unit_of_measure),
    num(raw.received_quantity),
    strOrNull(raw.target_branch_id),
    strOrNull(raw.variant_id),
  );
}

export async function upsertPulledReceptionLocal(
  raw: Record<string, unknown>,
  conn?: SqliteAsyncConnection,
): Promise<void> {
  const exec = conn ?? db;
  await exec.runAsync(
    `INSERT OR REPLACE INTO purchase_receptions (
      id, branch_id, purchase_order_id, supplier_id, supplier_name, status,
      received_at, received_by, notes, supplier_invoice_ref,
      voided_at, voided_by, void_reason, created_at, synced
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`,
    str(raw.id),
    str(raw.branch_id),
    strOrNull(raw.purchase_order_id),
    strOrNull(raw.supplier_id),
    strOrNull(raw.supplier_name),
    str(raw.status, 'active'),
    str(raw.received_at),
    strOrNull(raw.received_by),
    strOrNull(raw.notes),
    strOrNull(raw.supplier_invoice_ref),
    strOrNull(raw.voided_at),
    strOrNull(raw.voided_by),
    strOrNull(raw.void_reason),
    str(raw.created_at),
  );
}

export async function upsertPulledReceptionItemLocal(
  raw: Record<string, unknown>,
  conn?: SqliteAsyncConnection,
): Promise<void> {
  const exec = conn ?? db;
  await exec.runAsync(
    `INSERT OR REPLACE INTO purchase_reception_items (
      id, reception_id, purchase_order_item_id, product_id, product_name, product_sku,
      quantity, unit_cost, unit_of_measure, stock_lot_id, stock_movement_id, target_branch_id, returned_quantity, synced
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`,
    str(raw.id),
    str(raw.reception_id),
    strOrNull(raw.purchase_order_item_id),
    strOrNull(raw.product_id),
    strOrNull(raw.product_name),
    strOrNull(raw.product_sku),
    num(raw.quantity),
    num(raw.unit_cost),
    strOrNull(raw.unit_of_measure),
    strOrNull(raw.stock_lot_id),
    strOrNull(raw.stock_movement_id),
    strOrNull(raw.target_branch_id),
    num(raw.returned_quantity),
  );
}

export async function upsertPulledReturnLocal(
  raw: Record<string, unknown>,
  conn?: SqliteAsyncConnection,
): Promise<void> {
  const exec = conn ?? db;
  await exec.runAsync(
    `INSERT OR REPLACE INTO purchase_returns (
      id, branch_id, reception_id, supplier_id, supplier_name, status,
      returned_at, returned_by, reason, notes,
      voided_at, voided_by, void_reason, created_at, synced
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`,
    str(raw.id),
    str(raw.branch_id),
    strOrNull(raw.reception_id),
    strOrNull(raw.supplier_id),
    strOrNull(raw.supplier_name),
    str(raw.status, 'active'),
    str(raw.returned_at),
    strOrNull(raw.returned_by),
    strOrNull(raw.reason),
    strOrNull(raw.notes),
    strOrNull(raw.voided_at),
    strOrNull(raw.voided_by),
    strOrNull(raw.void_reason),
    str(raw.created_at),
  );
}

export async function upsertPulledReturnItemLocal(
  raw: Record<string, unknown>,
  conn?: SqliteAsyncConnection,
): Promise<void> {
  const exec = conn ?? db;
  await exec.runAsync(
    `INSERT OR REPLACE INTO purchase_return_items (
      id, return_id, reception_item_id, product_id, product_name, product_sku,
      quantity, unit_cost, unit_of_measure, stock_movement_id, synced
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`,
    str(raw.id),
    str(raw.return_id),
    strOrNull(raw.reception_item_id),
    strOrNull(raw.product_id),
    strOrNull(raw.product_name),
    strOrNull(raw.product_sku),
    num(raw.quantity),
    num(raw.unit_cost),
    strOrNull(raw.unit_of_measure),
    strOrNull(raw.stock_movement_id),
  );
}

export async function applyPurchasesPullLocal(_payload: PurchasesPullPayload): Promise<void> {
  // ADR-017 Fase 4: SQLite local eliminado — pull local es no-op, los datos vienen de Supabase.
}
